// =====================================================================
// Workspace Media — reusable per-workspace image store.
// =====================================================================
// Storage-only cost, no AI. PNG/JPEG only, 2 MB pre-resize, client
// resizes to 1920 px long edge, SHA-256 dedup per workspace, 24h
// soft-delete + restore. See mem://features/workspace-media.md.
// =====================================================================

import { supabase } from "@/integrations/supabase/client";
import { resizeImageForCover } from "@/lib/imageResize";

export const WORKSPACE_MEDIA_BUCKET = "workspace-media";
export const WORKSPACE_MEDIA_MAX_BYTES = 2 * 1024 * 1024; // 2 MB pre-resize
export const WORKSPACE_MEDIA_ALLOWED_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
]);

/** One week — same TTL we use for other private image buckets. */
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 7;

export type WorkspaceMediaRow = {
  id: string;
  workspace_id: string;
  uploaded_by: string;
  storage_path: string;
  sha256: string;
  bytes: number;
  mime: "image/png" | "image/jpeg";
  width: number | null;
  height: number | null;
  original_filename: string | null;
  alt_text: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type UploadWorkspaceMediaResult = {
  row: WorkspaceMediaRow;
  signedUrl: string;
  /** True when an existing dedup match was returned instead of a fresh upload. */
  deduplicated: boolean;
};

const COLUMNS =
  "id, workspace_id, uploaded_by, storage_path, sha256, bytes, mime, width, height, original_filename, alt_text, created_at, updated_at, deleted_at";

const extFor = (mime: "image/png" | "image/jpeg"): "png" | "jpg" =>
  mime === "image/png" ? "png" : "jpg";

const sha256Hex = async (bytes: ArrayBuffer): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const readImageDimensions = (file: Blob): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve(w && h ? { width: w, height: h } : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

export type UploadWorkspaceMediaInput = {
  workspaceId: string;
  userId: string;
  file: File;
  /** Optional tier cap in bytes. When set, the upload is rejected if the
   * workspace's live-row total + this file would exceed it. `null` = unlimited. */
  capBytes?: number | null;
};

/** Thrown when a workspace media upload would exceed the tier cap. */
export class WorkspaceMediaCapError extends Error {
  capBytes: number;
  usedBytes: number;
  incomingBytes: number;
  constructor(opts: { capBytes: number; usedBytes: number; incomingBytes: number }) {
    super(
      `Workspace Media storage cap reached (${formatBytesStatic(opts.usedBytes)} of ${formatBytesStatic(opts.capBytes)} used). Free up space or upgrade your plan.`,
    );
    this.name = "WorkspaceMediaCapError";
    this.capBytes = opts.capBytes;
    this.usedBytes = opts.usedBytes;
    this.incomingBytes = opts.incomingBytes;
  }
}

// Internal copy used inside the error constructor (formatBytes is declared
// below in the same module, so we duplicate it as a small static helper).
const formatBytesStatic = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Validate → resize → hash → dedup-check → upload → insert row.
 * Returns the row plus a signed URL ready for `<img src>`.
 */
export const uploadWorkspaceMedia = async ({
  workspaceId,
  userId,
  file,
  capBytes = null,
}: UploadWorkspaceMediaInput): Promise<UploadWorkspaceMediaResult> => {
  if (!WORKSPACE_MEDIA_ALLOWED_TYPES.has(file.type)) {
    throw new Error(
      `Unsupported image type: ${file.type || "unknown"}. Workspace Media accepts PNG or JPEG only.`,
    );
  }
  if (file.size > WORKSPACE_MEDIA_MAX_BYTES) {
    throw new Error(
      `Image is too large (max ${WORKSPACE_MEDIA_MAX_BYTES / (1024 * 1024)} MB).`,
    );
  }

  // Tier cap precheck (best-effort; the same row may be deduped below so this
  // is an upper-bound estimate against the original file size).
  if (typeof capBytes === "number" && capBytes > 0) {
    const used = await getWorkspaceMediaUsageBytes(workspaceId);
    if (used + file.size > capBytes) {
      throw new WorkspaceMediaCapError({ capBytes, usedBytes: used, incomingBytes: file.size });
    }
  }

  // Resize: max 1920 px long edge, JPEG q80 (PNG preserved when source is PNG with alpha).
  const resized = await resizeImageForCover(file, { maxEdge: 1920, quality: 0.8 });
  // resizeImageForCover may emit "image/jpeg" or "image/png" — both allowed here.
  if (resized.type !== "image/png" && resized.type !== "image/jpeg") {
    throw new Error(`Unexpected resized type ${resized.type}`);
  }
  const mime = resized.type as "image/png" | "image/jpeg";

  const bytes = await resized.arrayBuffer();
  const sha256 = await sha256Hex(bytes);

  // Dedup check (live rows only).
  const { data: existing, error: lookupError } = await supabase
    .from("workspace_media")
    .select(COLUMNS)
    .eq("workspace_id", workspaceId)
    .eq("sha256", sha256)
    .is("deleted_at", null)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (existing) {
    const signed = await signWorkspaceMediaUrl((existing as WorkspaceMediaRow).storage_path);
    return { row: existing as WorkspaceMediaRow, signedUrl: signed, deduplicated: true };
  }

  // Read dimensions (best-effort, non-fatal).
  const dims = await readImageDimensions(resized);

  const storagePath = `${workspaceId}/${sha256}.${extFor(mime)}`;

  const { error: uploadError } = await supabase.storage
    .from(WORKSPACE_MEDIA_BUCKET)
    .upload(storagePath, resized, { contentType: mime, upsert: false });
  // It is possible (race) that another tab uploaded the same path. If so, fall through
  // and re-query — storage RLS will still allow the read.
  if (uploadError && !/already exists|duplicate/i.test(uploadError.message)) {
    throw uploadError;
  }

  const { data: inserted, error: insertError } = await supabase
    .from("workspace_media")
    .insert({
      workspace_id: workspaceId,
      uploaded_by: userId,
      storage_path: storagePath,
      sha256,
      bytes: resized.size,
      mime,
      width: dims?.width ?? null,
      height: dims?.height ?? null,
      original_filename: file.name || null,
    })
    .select(COLUMNS)
    .single();
  if (insertError) {
    // Race: another tab inserted the same (workspace_id, sha256). Re-fetch the existing row.
    if (/duplicate key|workspace_media_dedup_live/.test(insertError.message)) {
      const { data: raced } = await supabase
        .from("workspace_media")
        .select(COLUMNS)
        .eq("workspace_id", workspaceId)
        .eq("sha256", sha256)
        .is("deleted_at", null)
        .single();
      if (raced) {
        const signed = await signWorkspaceMediaUrl((raced as WorkspaceMediaRow).storage_path);
        return { row: raced as WorkspaceMediaRow, signedUrl: signed, deduplicated: true };
      }
    }
    throw insertError;
  }

  const signed = await signWorkspaceMediaUrl((inserted as WorkspaceMediaRow).storage_path);
  return { row: inserted as WorkspaceMediaRow, signedUrl: signed, deduplicated: false };
};

/**
 * Resolve a single `workspace_media.id` to a fresh signed URL, batching the
 * row → storage_path lookup. Returns `null` when the row is missing or
 * soft-deleted.
 */
export const resolveSignedUrlByMediaId = async (
  mediaId: string,
): Promise<string | null> => {
  const map = await resolveSignedUrlsByMediaIds([mediaId]);
  return map[mediaId] ?? null;
};

/**
 * Batch-resolve workspace_media row ids to fresh signed URLs.
 * Live rows only (deleted_at IS NULL). Caps batches at 100 ids per call to
 * keep `createSignedUrls` happy.
 */
export const resolveSignedUrlsByMediaIds = async (
  mediaIds: string[],
): Promise<Record<string, string>> => {
  const ids = Array.from(new Set(mediaIds.filter(Boolean)));
  if (ids.length === 0) return {};
  const out: Record<string, string> = {};
  for (let i = 0; i < ids.length; i += 100) {
    const batch = ids.slice(i, i + 100);
    const { data: rows, error } = await supabase
      .from("workspace_media")
      .select("id, storage_path")
      .in("id", batch)
      .is("deleted_at", null);
    if (error || !rows || rows.length === 0) continue;
    const paths = rows.map((r) => r.storage_path).filter(Boolean);
    const signedMap = await signWorkspaceMediaUrls(paths);
    for (const row of rows) {
      const url = signedMap[row.storage_path];
      if (url) out[row.id] = url;
    }
  }
  return out;
};

export const signWorkspaceMediaUrl = async (storagePath: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(WORKSPACE_MEDIA_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data) throw error ?? new Error("Failed to sign workspace media URL");
  return data.signedUrl;
};

export const signWorkspaceMediaUrls = async (
  storagePaths: string[],
): Promise<Record<string, string>> => {
  const list = Array.from(new Set(storagePaths.filter(Boolean)));
  if (list.length === 0) return {};
  const { data, error } = await supabase.storage
    .from(WORKSPACE_MEDIA_BUCKET)
    .createSignedUrls(list, SIGNED_URL_TTL_SECONDS);
  if (error || !data) return {};
  const out: Record<string, string> = {};
  for (const row of data) {
    if (row.path && row.signedUrl) out[row.path] = row.signedUrl;
  }
  return out;
};

export const listWorkspaceMedia = async (
  workspaceId: string,
  options: { limit?: number; includeDeleted?: boolean } = {},
): Promise<WorkspaceMediaRow[]> => {
  let query = supabase
    .from("workspace_media")
    .select(COLUMNS)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(options.limit ?? 200);
  if (!options.includeDeleted) query = query.is("deleted_at", null);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as WorkspaceMediaRow[];
};

export const softDeleteWorkspaceMedia = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("workspace_media")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

export const restoreWorkspaceMedia = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("workspace_media")
    .update({ deleted_at: null })
    .eq("id", id);
  if (error) throw error;
};


/** Sum of `bytes` across live rows for a workspace (drives the cap meter). */
export const getWorkspaceMediaUsageBytes = async (workspaceId: string): Promise<number> => {
  // Postgres has no `sum` head-style; do a small page-fetch and aggregate.
  const { data, error } = await supabase
    .from("workspace_media")
    .select("bytes")
    .eq("workspace_id", workspaceId)
    .is("deleted_at", null);
  if (error) throw error;
  return (data ?? []).reduce((acc, row) => acc + (row.bytes ?? 0), 0);
};

export const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
